import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { Col, Form, FormInstance, Row, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import useSwr from '@hooks/useSwr';
import SessionContext from '@providers/Session/SessionContext';

type SelectOptionProps = {
  label: string;
  value: any;
};

type ContractOptionProps = {
  assetNumber: string;
  nickname: string;
  planName: string;
  childId: null | string;
  id: string;
};

interface FormItemsValuesProps {
  contractMotId?: string;
  contractLorawanId?: string;
  requestId?: number;
}

interface SelectMotLoraContractsProps {
  form: FormInstance<FormItemsValuesProps>;
  onChange?: (values: FormItemsValuesProps) => void;
  span?: number;
}

const SelectMotLoraContracts = ({
  form,
  onChange,
  span = 20,
}: SelectMotLoraContractsProps) => {
  const { t } = useTranslation(['requests', 'attributes', 'lorawan']);
  const { keycloak } = useContext(KeycloakContext);
  const { isNotChildOfRoot } = useContext(SessionContext);
  const customerType = keycloak.idTokenParsed?.customerType;
  const isBroker = customerType === 'BROKER';
  const formValues = Form.useWatch([], form);

  useEffect(() => {
    if (formValues && onChange) {
      onChange?.(formValues);
    }
  }, [formValues, onChange]);

  const { data: motContracts } = useSwr(
    isBroker ? '/service-proxy/contracts/hierarchyMOT' : null,
    {
      params: {
        acronym: 'MOT_LORAWAN',
      },
    },
  );

  const { data: loraWanContracts } = useSwr(
    isBroker ? '/service-proxy/contracts' : null,
    {
      params: {
        product: 'LORAWAN',
      },
    },
  );

  const { data: requests } = useSwr('/lorawan/virtual-accounts/active');

  const handleSelectOptions = useCallback(
    (contractsList: ContractOptionProps[]) => {
      const hasNickname = (contract: ContractOptionProps) =>
        contract?.nickname ? `${contract?.nickname} -` : '';

      return Array.isArray(contractsList)
        ? contractsList?.map(contract => ({
            label: `${contract?.assetNumber} - ${hasNickname(contract)} ${
              contract?.planName
            }`,
            value: contract?.childId ?? contract?.id,
          }))
        : [];
    },
    [],
  );

  const motContractsOptions = useMemo(
    () => handleSelectOptions(motContracts),
    [handleSelectOptions, motContracts],
  );

  const loraWanContractsOptions = useMemo(
    () => handleSelectOptions(loraWanContracts),
    [handleSelectOptions, loraWanContracts],
  );

  const requestsOptions = useMemo(
    () =>
      Array.isArray(requests)
        ? requests?.map((request: any) => ({
            label: `${request?.id} - ${request?.name}`,
            value: request?.id,
          }))
        : [],
    [requests],
  );

  const formContractLabel = useCallback(
    (label: string) => (
      <>
        {t('attributes:contract')}
        <span style={{ marginLeft: 4, fontWeight: 'bold' }}>{label}</span>
      </>
    ),
    [t],
  );

  const hasSingleOption = useCallback(
    (options: SelectOptionProps[]) => options?.length === 1,
    [],
  );

  const getFirstValueWhenHasSingleOption = useCallback(
    (options: SelectOptionProps[]) => options?.[0]?.value,
    [],
  );

  const setFieldIfSingleOption = useCallback(
    (options: SelectOptionProps[], field: string) => {
      if (hasSingleOption(options)) {
        form?.setFieldValue(field, getFirstValueWhenHasSingleOption(options));
      }
    },
    [form, getFirstValueWhenHasSingleOption, hasSingleOption],
  );

  useEffect(() => {
    setFieldIfSingleOption(motContractsOptions, 'contractMotId');
    setFieldIfSingleOption(loraWanContractsOptions, 'contractLorawanId');
    setFieldIfSingleOption(requestsOptions, 'requestId');
  }, [
    form,
    getFirstValueWhenHasSingleOption,
    hasSingleOption,
    loraWanContractsOptions,
    motContractsOptions,
    requestsOptions,
    setFieldIfSingleOption,
  ]);

  return (
    <Row gutter={[8, 8]} justify="center">
      {isBroker && !isNotChildOfRoot ? (
        <>
          <Col span={span}>
            <Form.Item
              name="contractMotId"
              label={formContractLabel('MoT')}
              rules={[
                {
                  required: true,
                  message: t('lorawan:fieldIsRequired'),
                },
              ]}
            >
              <Select
                optionFilterProp="label"
                options={motContractsOptions}
                disabled={
                  !motContracts ||
                  !motContractsOptions ||
                  hasSingleOption(motContractsOptions)
                }
                loading={!motContracts}
                showSearch
              />
            </Form.Item>
          </Col>
          <Col span={span}>
            <Form.Item
              name="contractLorawanId"
              label={formContractLabel('LoraWan')}
              rules={[
                {
                  required: true,
                  message: t('lorawan:fieldIsRequired'),
                },
              ]}
            >
              <Select
                optionFilterProp="label"
                options={loraWanContractsOptions}
                disabled={
                  !loraWanContracts ||
                  !loraWanContractsOptions ||
                  hasSingleOption(loraWanContractsOptions)
                }
                loading={!loraWanContracts}
                showSearch
              />
            </Form.Item>
          </Col>
        </>
      ) : (
        <Col span={span}>
          <Form.Item
            name="requestId"
            label={t('attributes:baseRequest')}
            rules={[
              {
                required: true,
                message: t('lorawan:fieldIsRequired'),
              },
            ]}
          >
            <Select
              optionFilterProp="label"
              options={requestsOptions}
              disabled={!requestsOptions || hasSingleOption(requestsOptions)}
              loading={!requests}
              showSearch
            />
          </Form.Item>
        </Col>
      )}
    </Row>
  );
};

export default SelectMotLoraContracts;
